import { z } from 'zod';
import {
    NavigationNodeSchema,
    GetNavigationNodeListingSchema,
} from '../schemas/navigationNodeZodSchemas';
import { NavigationNode } from '../types/NavigationNode';

/**
 * Transforms and validates the response from the getNavigationNodeListing query
 * into a simplified NavigationNode[] with correctly hydrated parent references.
 *
 * @param data The response data from the getNavigationNodeListing query.
 * @returns An array of valid NavigationNode objects.
 */
export function transformNodeListingResponse(data: unknown): NavigationNode[] {
    // Validate and transform the response using the Zod schema
    const parsedResult = GetNavigationNodeListingSchema.safeParse(data);
    if (!parsedResult.success) {
        console.error('Validation failed for getNavigationNodeListing:', parsedResult.error.errors);
        return [];
    }
    const edges = parsedResult.data.getNavigationNodeListing.edges;

    // Define an intermediate type that includes a temporary parentId.
    type IntermediateNavigationNode = Omit<NavigationNode, 'parent'> & {
        parentId: string;
        parent?: IntermediateNavigationNode;
    };

    const prefix = '/Site Navigation';

    // First pass: build a map of nodes with their parentId
    const nodeMap: Record<string, IntermediateNavigationNode> = {};
    for (const { node } of edges) {
        const n = node as z.infer<typeof NavigationNodeSchema>;
        nodeMap[n.id] = {
            id: n.id,
            key: n.key,
            index: n.index,
            path: n.fullpath.startsWith(prefix) ? n.fullpath.slice(prefix.length) : n.fullpath,
            name: n.name,
            route: n.route,
            parentId: n.parentNode.id,
            menuProperties: {
                isGlobalMenuItem: n.isGlobalMenuItem,
                additionalMenuItemsSource: n.additionalMenuItemsSource,
            },
        };
    }

    // Second pass: replace the temporary parentId with the actual parent reference.
    // Skip nodes with non-existent parentIds.
    for (const node of Object.values(nodeMap)) {
        const parentId = node.parentId;
        if (parentId in nodeMap) {
            node.parent = nodeMap[parentId];
        } else {
            console.warn(
                `NavigationNode with id ${node.id} has a non-existent parentId: ${parentId}. Skipping.`
            );
        }
    }

    // Remove the temporary parentId field to produce the final result.
    const finalNodes: NavigationNode[] = Object.values(nodeMap)
        .filter(({ parent }) => parent !== undefined)
        .map(({ parentId, ...node }) => node as NavigationNode);
    return finalNodes;
}
